import { useMemo } from "react";
import {
  MessageCircle,
  PhoneCall,
  Phone,
  Download,
} from "lucide-react";
import {
  phoneToWa,
  phoneToWaCall,
  phoneToTel,
  formatPhoneDisplay,
  downloadVCard,
} from "../lib/contact";

/**
 * Wave 6 — universal contact buttons cluster.
 *
 * Renders three actions for any phone number:
 *   • WhatsApp chat (always visible)
 *   • WhatsApp call (mobile-only — `whatsapp://call?phone=…` deep-link)
 *   • Carrier call (mobile-only — `tel:+…`)
 *
 * Plus an optional "Save contact" (vCard) button.
 *
 * `compact` shrinks paddings + hides labels for table rows.
 * `mobileOnlyExtras` keeps the WA-call + carrier-call buttons hidden on
 *   desktop (where they're useless) — the WhatsApp chat button stays.
 */
export default function ContactActions({
  phone,
  email,
  name,
  org,
  title,
  compact = false,
  showVCard = false,
  mobileOnlyExtras = true,
  testIdPrefix = "contact",
  className = "",
}) {
  const wa = useMemo(() => phoneToWa(phone), [phone]);
  const waCall = useMemo(() => phoneToWaCall(phone), [phone]);
  const tel = useMemo(() => phoneToTel(phone), [phone]);

  if (!phone && !email && !showVCard) return null;

  const btnBase = `inline-flex items-center justify-center gap-1 rounded-md border transition shrink-0 ${
    compact ? "px-1.5 py-1 text-[11px]" : "px-2.5 py-1.5 text-xs"
  }`;

  return (
    <div className={`flex items-center gap-1.5 flex-wrap ${className}`} data-testid={`${testIdPrefix}-actions`}>
      {wa && (
        <a
          href={wa}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className={`${btnBase} border-emerald-500/30 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20`}
          data-testid={`${testIdPrefix}-wa`}
          title={`WhatsApp ${formatPhoneDisplay(phone)}`}
        >
          <MessageCircle className="w-3.5 h-3.5" />
          {!compact && <span>WhatsApp</span>}
        </a>
      )}
      {waCall && (
        <a
          href={waCall}
          onClick={(e) => e.stopPropagation()}
          className={`${btnBase} border-emerald-500/30 bg-emerald-500/5 text-emerald-300 hover:bg-emerald-500/15 ${
            mobileOnlyExtras ? "md:hidden" : ""
          }`}
          data-testid={`${testIdPrefix}-wa-call`}
          title={`WhatsApp call ${formatPhoneDisplay(phone)}`}
        >
          <PhoneCall className="w-3.5 h-3.5" />
          {!compact && <span>WA call</span>}
        </a>
      )}
      {tel && (
        <a
          href={tel}
          onClick={(e) => e.stopPropagation()}
          className={`${btnBase} border-cyan-500/30 bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20 ${
            mobileOnlyExtras ? "md:hidden" : ""
          }`}
          data-testid={`${testIdPrefix}-call`}
          title={`Call ${formatPhoneDisplay(phone)}`}
        >
          <Phone className="w-3.5 h-3.5" />
          {!compact && <span>Call</span>}
        </a>
      )}
      {showVCard && (phone || email) && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            downloadVCard({ name, phone, email, org, title });
          }}
          className={`${btnBase} border-white/10 text-[#a0aabf] hover:border-white/30 hover:text-white`}
          data-testid={`${testIdPrefix}-vcard`}
          title="Save to phone (.vcf)"
        >
          <Download className="w-3.5 h-3.5" />
          {!compact && <span>Save</span>}
        </button>
      )}
    </div>
  );
}
